import React from "react";
import { Phone, MapPin, Instagram, ArrowUpRight } from "lucide-react";
import { SiInstagram, SiFacebook, SiTiktok, SiUbereats, SiDoordash } from "react-icons/si";
import { BRAND, SOCIAL, ORDER, NAV } from "../../data/content";
import { useOrder } from "../../context/OrderContext";

const delivery = [
  { label: "Uber Eats", href: ORDER.ubereats, icon: <SiUbereats /> },
  { label: "DoorDash", href: ORDER.doordash, icon: <SiDoordash /> },
  { label: "SkipTheDishes", href: ORDER.skipthedishes, icon: <span className="font-display font-bold">S</span> },
  { label: "Fantuan", href: ORDER.fantuan, icon: <span className="font-display font-bold">福</span> },
];

const socials = [
  { label: "Instagram", href: SOCIAL.instagram, icon: <SiInstagram /> },
  { label: "Facebook", href: SOCIAL.facebook, icon: <SiFacebook /> },
  { label: "TikTok", href: SOCIAL.tiktok, icon: <SiTiktok /> },
];

export const Footer = () => {
  const { openOrder } = useOrder();
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer className="relative border-t border-white/8 bg-surface pt-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-2 gap-10 sm:grid-cols-4 lg:grid-cols-5">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-2">
            <div className="flex items-center gap-3">
              <img src={BRAND.logo} alt="Smokey & Creamy" className="h-12 w-12 rounded-full object-cover ring-1 ring-gold/40" />
              <span className="text-gold-gloss font-display text-xl font-semibold">Smokey &amp; Creamy</span>
            </div>
            <p className="mt-5 max-w-sm text-sm text-cream/55 leading-relaxed">{BRAND.descriptionShort}</p>
            <p className="mt-4 text-gold-gloss font-display text-lg font-semibold">{BRAND.kicker}</p>
            <div className="mt-6 flex gap-3">
              {socials.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`footer-${s.label.toLowerCase()}`}
                  className="grid h-11 w-11 place-items-center rounded-full border border-white/12 text-cream/80 hover:border-gold/40 hover:text-gold transition-colors text-lg">
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Explore */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cream/45">Explore</p>
            <ul className="mt-4 space-y-3">
              {NAV.map((n) => (
                <li key={n.href}>
                  <button onClick={() => go(n.href)} className="text-sm text-cream/70 hover:text-gold transition-colors">{n.label}</button>
                </li>
              ))}
              <li><button onClick={openOrder} data-testid="footer-order" className="text-sm text-cream/70 hover:text-gold transition-colors">Order Now</button></li>
            </ul>
          </div>

          {/* Delivery */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cream/45">Delivery</p>
            <ul className="mt-4 space-y-3">
              {delivery.map((d) => (
                <li key={d.label}>
                  <a href={d.href} target="_blank" rel="noreferrer" data-testid={`footer-delivery-${d.label.replace(/\s/g, "").toLowerCase()}`}
                    className="group inline-flex items-center gap-2 text-sm text-cream/70 hover:text-gold transition-colors">
                    <span className="text-base">{d.icon}</span>{d.label}
                    <ArrowUpRight size={13} className="opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-2 sm:col-span-1">
            <p className="text-xs uppercase tracking-[0.2em] text-cream/45">Visit</p>
            <a href={BRAND.mapsDirections} target="_blank" rel="noreferrer" className="mt-4 flex items-start gap-2 text-sm text-cream/70 hover:text-gold transition-colors">
              <MapPin size={16} className="mt-0.5 shrink-0" />
              <span>{BRAND.addressLine1}<br />{BRAND.addressLine2}</span>
            </a>
            <a href={BRAND.phoneHref} className="mt-3 flex items-center gap-2 text-sm text-cream/70 hover:text-gold transition-colors">
              <Phone size={16} /> {BRAND.phoneDisplay}
            </a>
            <p className="mt-3 text-xs text-cream/45">{BRAND.hours}</p>
          </div>
        </div>

        {/* Giant wordmark */}
        <div className="relative mt-16">
          <h2 className="select-none text-gold-gloss font-display font-semibold tracking-tight text-[8.5vw] lg:text-[7rem] whitespace-nowrap leading-[1.15] pb-2">
            Smokey &amp; Creamy
          </h2>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/8 py-7 text-xs text-cream/45 sm:flex-row">
          <p>© {new Date().getFullYear()} Smokey &amp; Creamy. All rights reserved.</p>
          <p className="flex items-center gap-1.5"><span className="inline-block h-1.5 w-1.5 rounded-full bg-gold" /> 100% Halal · Winnipeg, MB</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
